import history from "App/History";
import { CONFIG } from "core/config";
import { isLoggedIn } from "core/services/Auth";
import { getHomePage } from "core/services/RBAC";
import { useEffect, useState } from "react";
import { Field, InjectedFormProps, reduxForm } from "redux-form";
import ShadedInput from "shared/components/redux-form/ShadedInput";
import EyeOpen from "shared/Icons/EyeOpen";
import EyeClose from "shared/Icons/EyeClose";
import validate from "./validate";
import ArenaLogo from "assets/icons/ArenaEsports.png";
import { Button } from "rsuite";
import googleLogo from "../../assets/icons/logo-google.png";
import moderatorLogo from "../../assets/icons/tag-user.png";

interface FormData {
  name: string;
  email: string;
  password: string;
}

interface SignUpProps {
  onPageSwitch?(page: string): void;
  onSignUp?(data: any): void;
}

type FinalProps = InjectedFormProps<FormData, SignUpProps> & SignUpProps;

function SignUp({ handleSubmit, onPageSwitch, onSignUp }: FinalProps) {
  const [passwordHidden, setPasswordHidden] = useState(true);

  useEffect(() => {
    if (isLoggedIn()) {
      history.replace(getHomePage());
    }
  }, []);

  function handleGoogleSignup() {
    const gauthUrl = new URL(CONFIG.gauthOAuth);
    gauthUrl.searchParams.append("client_id", CONFIG.gauthClientId);
    gauthUrl.searchParams.append(
      "redirect_uri",
      `${window.location.origin}${CONFIG.redirectUrl}`
    );
    gauthUrl.searchParams.append("response_type", "code");
    gauthUrl.searchParams.append("scope", "email profile openid");
    gauthUrl.searchParams.append("state", "signup");
    window.location.href = gauthUrl.toString();
  }

  function handleForm(formData: FormData) {
    if (onSignUp) {
      onSignUp({ ...formData, loginType: "email" });
    }
  }

  function togglePasswordVisibility() {
    setPasswordHidden((prev) => !prev);
  }

  function handlePageSwitch() {
    if (onPageSwitch) {
      onPageSwitch("login");
    }
  }

  return (
    <div className="sign-up-container">
      <div className="logo-container">
        <img src={ArenaLogo} alt="Arena Logo" className="arena-logo" />
      </div>
      <div className="welcome-text">Welcome to Hub!</div>
      <div className="sub-head">Create new Account</div>
      <form
        className="form-wrapper"
        autoComplete="off"
        onSubmit={handleSubmit(handleForm)}
      >
        <div className="sign-up-control">
          <Field
            name="name"
            component={ShadedInput}
            label="Name"
            placeholder="Enter Full Name"
            props={{
              className: "shaded-blue",
              labelClassName: "label-white",
            }}
          />
        </div>
        <div className="sign-up-control">
          <Field
            name="email"
            component={ShadedInput}
            label="Email"
            placeholder="Enter Email"
            props={{
              className: "shaded-blue",
              labelClassName: "label-white",
            }}
          />
        </div>
        <div className="sign-up-control">
          <Field
            name="password"
            type={passwordHidden ? "password" : "text"}
            component={ShadedInput}
            label="Password"
            placeholder="Create Password"
            autoComplete="new-password"
            icon={
              passwordHidden ? (
                <EyeClose fontSize={20} />
              ) : (
                <EyeOpen fontSize={18} />
              )
            }
            withButton={true}
            onClick={togglePasswordVisibility}
            props={{
              className: "shaded-blue",
              labelClassName: "label-white",
            }}
          />
        </div>
        <Button block type="submit" className="sign-up-btn">
          Signup
        </Button>
      </form>
      <div className="switch-sign-up">
        <span>Have an account?</span>
        <span className="sign-in" onClick={handlePageSwitch}>
          Sign in
        </span>
      </div>
      <div className="third-part-sign-in-container">
        <div className="login-text">
          <span className="hr-line"></span>
          <span>Or login with</span>
          <span className="hr-line"></span>
        </div>
        <div className="third-part-sign-in-btns" onClick={handleGoogleSignup}>
          <img src={googleLogo} alt="google-logo" /> Sign up with Google
        </div>
        <a href={CONFIG.moderatorUrl} className="third-part-sign-in-btns">
          <img src={moderatorLogo} alt="moderator-logo" />
          Moderator Sign In
        </a>
      </div>
    </div>
  );
}

export default reduxForm<FormData, SignUpProps>({
  form: "hub-signup",
  validate,
  shouldValidate: () => true,
})(SignUp);
